// services/contactMessageService.ts
import { supabase } from './supabaseClient';

export interface ContactFormData {
    name: string;
    email: string;
    subject: string;
    message: string;
    senderUserId?: string; // If user is logged in
}

export interface ContactMessage {
    id: string;
    message_id: string;
    sender_user_id: string | null;
    recipient_user_id: string;
    sender_email: string | null;
    sender_name: string | null;
    subject_category: string;
    is_read: boolean;
    replied_at: string | null;
    reply_message_id: string | null;
    created_at: string;
    message?: {
        message_text: string;
        message_type: string;
        background_color: string;
        text_color: string;
    };
}

/**
 * Service for handling contact form submissions as website messages
 */
export class ContactMessageService {

    // Email of the user who receives all contact messages
    private static readonly CONTACT_RECIPIENT_EMAIL = 'SeedDots@outlook.com';

    /**
     * Create a contact message from the contact form
     */
    static async createContactMessage(formData: ContactFormData): Promise<{ success: boolean; error?: string }> {
        try {
            // Get the recipient user ID (SeedDots@outlook.com)
            const { data: recipientUser, error: recipientError } = await supabase
                .from('users')
                .select('id')
                .eq('email', this.CONTACT_RECIPIENT_EMAIL)
                .single();

            if (recipientError || !recipientUser) {
                console.error('Recipient user not found:', recipientError);
                return { success: false, error: 'Contact recipient not configured' };
            }

            // Get current user if logged in
            const { data: { user } } = await supabase.auth.getUser();
            const senderUserId = user?.id || formData.senderUserId || null;

            // Create the website message
            const messageText = this.formatContactMessage(formData);
            const { data: websiteMessage, error: messageError } = await supabase
                .from('website_messages')
                .insert({
                    message_text: messageText,
                    message_type: 'contact_form',
                    background_color: '#fef3c7', // Amber background
                    text_color: '#92400e', // Dark amber text
                    start_date: new Date().toISOString(),
                    end_date: null,
                    is_active: true,
                })
                .select()
                .single();

            if (messageError || !websiteMessage) {
                console.error('Error creating website message:', messageError);
                return { success: false, error: 'Failed to create message' };
            }

            // Create the contact message routing entry
            const { error: contactError } = await supabase
                .from('contact_messages')
                .insert({
                    message_id: websiteMessage.id,
                    sender_user_id: senderUserId,
                    recipient_user_id: recipientUser.id,
                    sender_email: !senderUserId ? formData.email : null,
                    sender_name: !senderUserId ? formData.name : null,
                    subject_category: formData.subject,
                    is_read: false,
                });

            if (contactError) {
                console.error('Error creating contact message:', contactError);
                // Clean up the website message if contact message creation fails
                await supabase.from('website_messages').delete().eq('id', websiteMessage.id);
                return { success: false, error: 'Failed to route message' };
            }

            return { success: true };
        } catch (error) {
            console.error('Error in createContactMessage:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }

    /**
     * Get contact messages for the current user (SeedDots user)
     */
    static async getContactMessages(userId: string): Promise<ContactMessage[]> {
        try {
            const { data, error } = await supabase
                .from('contact_messages')
                .select(`
          *,
          message:website_messages(
            message_text,
            message_type,
            background_color,
            text_color
          )
        `)
                .eq('recipient_user_id', userId)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching contact messages:', error);
                return [];
            }

            return data || [];
        } catch (error) {
            console.error('Error in getContactMessages:', error);
            return [];
        }
    }

    /**
     * Mark a contact message as read
     */
    static async markAsRead(contactMessageId: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('contact_messages')
                .update({ is_read: true })
                .eq('id', contactMessageId);

            if (error) {
                console.error('Error marking message as read:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error in markAsRead:', error);
            return false;
        }
    }

    /**
     * Reply to a contact message
     */
    static async replyToContactMessage(
        contactMessageId: string,
        replyText: string
    ): Promise<{ success: boolean; error?: string }> {
        try {
            // Get the contact message details
            const { data: contactMessage, error: fetchError } = await supabase
                .from('contact_messages')
                .select('sender_user_id, sender_email, sender_name, subject_category')
                .eq('id', contactMessageId)
                .single();

            if (fetchError || !contactMessage) {
                console.error('Contact message not found:', fetchError);
                return { success: false, error: 'Contact message not found' };
            }

            // Create reply message
            const replyMessageText = this.formatReplyMessage(
                contactMessage.subject_category,
                replyText
            );

            const { data: replyMessage, error: replyError } = await supabase
                .from('website_messages')
                .insert({
                    message_text: replyMessageText,
                    message_type: 'contact_reply',
                    background_color: '#dbeafe', // Blue background
                    text_color: '#1e40af', // Dark blue text
                    start_date: new Date().toISOString(),
                    end_date: null,
                    is_active: true,
                })
                .select()
                .single();

            if (replyError || !replyMessage) {
                console.error('Error creating reply message:', replyError);
                return { success: false, error: 'Failed to create reply' };
            }

            // If sender was logged in, create a contact message for them
            if (contactMessage.sender_user_id) {
                const { data: { user } } = await supabase.auth.getUser();

                await supabase
                    .from('contact_messages')
                    .insert({
                        message_id: replyMessage.id,
                        sender_user_id: user?.id || null,
                        recipient_user_id: contactMessage.sender_user_id,
                        sender_email: null,
                        sender_name: null,
                        subject_category: `Re: ${contactMessage.subject_category}`,
                        is_read: false,
                    });
            }

            // Update original contact message with reply info
            const { error: updateError } = await supabase
                .from('contact_messages')
                .update({
                    replied_at: new Date().toISOString(),
                    reply_message_id: replyMessage.id,
                })
                .eq('id', contactMessageId);

            if (updateError) {
                console.error('Error updating contact message:', updateError);
            }

            return { success: true };
        } catch (error) {
            console.error('Error in replyToContactMessage:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }

    /**
     * Format contact form data into a readable message
     */
    private static formatContactMessage(formData: ContactFormData): string {
        return `<div class="contact-message-container" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 100%; line-height: 1.6;">
  <div class="contact-message-header" style="background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%); padding: 20px; border-radius: 12px; margin-bottom: 24px; border: 1px solid #FCD34D;">
    <h3 style="margin: 0 0 8px 0; color: #92400E; font-size: 20px; font-weight: 700; display: flex; align-items: center; gap: 8px;">
      <span style="font-size: 24px;">📬</span>
      New Contact Form Submission
    </h3>
    <p style="margin: 0; color: #78350F; font-size: 14px; opacity: 0.9;">
      <strong>Received:</strong> ${new Date().toLocaleString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      })}
    </p>
  </div>
  
  <div class="contact-message-body" style="background: #FFFFFF; border: 2px solid #E5E7EB; border-radius: 12px; padding: 24px; margin-bottom: 20px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
    <div style="margin-bottom: 20px;">
      <div style="color: #6B7280; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; font-weight: 600;">👤 From</div>
      <div style="color: #111827; font-size: 17px; font-weight: 600; padding: 8px 0;">${this.escapeHtml(formData.name)}</div>
    </div>
    
    <div style="margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #F3F4F6;">
      <div style="color: #6B7280; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; font-weight: 600;">✉️ Email</div>
      <div style="color: #111827; font-size: 16px; padding: 8px 0;">
        <a href="mailto:${formData.email}" style="color: #2563EB; text-decoration: none; font-weight: 500; transition: color 0.2s;" onmouseover="this.style.color='#1D4ED8'" onmouseout="this.style.color='#2563EB'">
          ${this.escapeHtml(formData.email)}
        </a>
      </div>
    </div>
    
    <div style="margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #F3F4F6;">
      <div style="color: #6B7280; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; font-weight: 600;">📋 Subject Category</div>
      <div style="color: #111827; font-size: 16px; font-weight: 600; padding: 8px 12px; background: #F3F4F6; border-radius: 6px; display: inline-block;">
        ${this.escapeHtml(formData.subject)}
      </div>
    </div>
    
    <div>
      <div style="color: #6B7280; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; font-weight: 600;">💬 Message</div>
      <div style="color: #1F2937; font-size: 15px; line-height: 1.8; white-space: pre-wrap; padding: 16px; background: #F9FAFB; border-radius: 8px; border-left: 4px solid #FCD34D;">
        ${this.escapeHtml(formData.message)}
      </div>
    </div>
  </div>
  
  <div class="contact-message-footer" style="background: #F9FAFB; padding: 16px; border-radius: 8px; border: 1px solid #E5E7EB; text-align: center;">
    <p style="margin: 0; color: #6B7280; font-size: 13px;">
      💡 <em>Use the Reply button below to respond to this contact message</em>
    </p>
  </div>
</div>`;
    }

    /**
     * Format reply message with improved styling
     */
    private static formatReplyMessage(originalSubject: string, replyText: string): string {
        return `<div class="contact-reply-container" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 100%; line-height: 1.6;">
  <div class="contact-reply-header" style="background: linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%); padding: 20px; border-radius: 12px; margin-bottom: 24px; border: 1px solid #93C5FD;">
    <h3 style="margin: 0 0 8px 0; color: #1E40AF; font-size: 20px; font-weight: 700; display: flex; align-items: center; gap: 8px;">
      <span style="font-size: 24px;">💬</span>
      Reply to Your Contact Form
    </h3>
    <p style="margin: 0; color: #1E3A8A; font-size: 14px; opacity: 0.9;">
      <strong>Sent:</strong> ${new Date().toLocaleString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      })}
    </p>
  </div>
  
  <div class="contact-reply-subject" style="background: #FFFFFF; border: 2px solid #E5E7EB; border-radius: 12px; padding: 16px 24px; margin-bottom: 20px;">
    <div style="color: #6B7280; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; font-weight: 600;">📋 Re: Subject</div>
    <div style="color: #111827; font-size: 16px; font-weight: 600;">
      ${this.escapeHtml(originalSubject)}
    </div>
  </div>
  
  <div class="contact-reply-message" style="background: #FFFFFF; border: 2px solid #E5E7EB; border-radius: 12px; padding: 24px; margin-bottom: 20px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
    <div style="color: #6B7280; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; font-weight: 600;">💬 Response</div>
    <div style="color: #1F2937; font-size: 15px; line-height: 1.8; white-space: pre-wrap; padding: 16px; background: #F9FAFB; border-radius: 8px; border-left: 4px solid #93C5FD;">
      ${this.escapeHtml(replyText)}
    </div>
  </div>
  
  <div class="contact-reply-footer" style="background: linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%); padding: 16px; border-radius: 8px; border: 1px solid #BAE6FD; text-align: center;">
    <p style="margin: 0 0 4px 0; color: #0C4A6E; font-size: 13px; font-weight: 600;">
      ✨ SeedDots Support Team
    </p>
    <p style="margin: 0; color: #075985; font-size: 12px; opacity: 0.8;">
      Thank you for contacting us!
    </p>
  </div>
</div>`;
    }

    /**
     * Escape HTML to prevent XSS attacks
     */
    private static escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}
